/* Sturdy mystery gift popup.
   Step 1 collects the profile, step 2 verifies the mobile by SMS, step 3 reveals
   a discount code minted for that subscriber. Everything that matters — code
   generation, consent, the Maropost write — happens in the middleware. */
(function () {
  "use strict";

  var STORE_KEY = "sturdy_gift_popup";
  var SESSION_KEY = "sturdy_gift_pending";
  var RESEND_SECONDS = 30;

  var root = document.getElementById("sturdy-subscribe-popup");
  if (!root) { return; }

  var config = {
    api: (root.dataset.apiBase || "").replace(/\/+$/, ""),
    scrollPercent: parseInt(root.dataset.scrollPercent, 10) || 20,
    dismissDays: parseInt(root.dataset.dismissDays, 10) || 1,
    minSeconds: parseInt(root.dataset.minSeconds, 10) || 0,
    countryCode: root.dataset.countryCode || "61",
    testMode: root.dataset.testMode === "true"
  };

  /* ---------------------------------------------------------------- state */

  function readState() {
    try { return JSON.parse(window.localStorage.getItem(STORE_KEY)) || {}; }
    catch (error) { return {}; }
  }

  function writeState(patch) {
    var next = Object.assign(readState(), patch);
    try { window.localStorage.setItem(STORE_KEY, JSON.stringify(next)); } catch (error) { /* private mode */ }
    return next;
  }

  function readPending() {
    try { return JSON.parse(window.sessionStorage.getItem(SESSION_KEY)); }
    catch (error) { return null; }
  }

  function writePending(value) {
    try {
      if (value) { window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(value)); }
      else { window.sessionStorage.removeItem(SESSION_KEY); }
    } catch (error) { /* private mode */ }
  }

  /* Suppressed when: already subscribed, dismissed inside the cool-off window,
     or the visitor asked for no popup with ?nopopup=1. Test mode ignores all
     three so the theme editor can keep reopening it. */
  function suppressed() {
    if (config.testMode) { return false; }
    if (window.location.search.indexOf("nopopup=1") > -1) { return true; }
    var state = readState();
    if (state.subscribed) { return true; }
    if (state.dismissedUntil && Date.now() < state.dismissedUntil) { return true; }
    return false;
  }

  /* ------------------------------------------------------------- elements */

  var dialog = root.querySelector(".sr-popup__dialog");
  var panes = {
    1: root.querySelector('[data-pane="1"]'),
    2: root.querySelector('[data-pane="2"]'),
    3: root.querySelector('[data-pane="3"]'),
    4: root.querySelector('[data-pane="4"]')
  };
  var stepItems = root.querySelectorAll(".sr-steps__item");
  var detailsForm = root.querySelector('[data-role="details-form"]');
  var verifyForm = root.querySelector('[data-role="verify-form"]');
  var profileForm = root.querySelector('[data-role="profile-form"]');
  var otpInputs = Array.prototype.slice.call(root.querySelectorAll('[data-role="otp"] input'));
  var resendButton = root.querySelector('[data-action="resend"]');
  var maskedPhone = root.querySelector('[data-role="masked-phone"]');

  /* gift is issued at verification but shown after step 3, so it waits here. */
  var session = { id: null, gift: null, resendTimer: null, opened: false, lastFocus: null };

  /* ---------------------------------------------------------------- utils */

  function message(pane, text, tone) {
    var node = panes[pane].querySelector('[data-role="message"]');
    if (!node) { return; }
    node.textContent = text || "";
    node.setAttribute("data-tone", tone || "error");
  }

  function busy(button, active, label) {
    if (!button.dataset.label) { button.dataset.label = button.textContent; }
    button.disabled = active;
    if (active) {
      button.innerHTML = "";
      var spinner = document.createElement("span");
      spinner.className = "sr-button__spinner";
      button.appendChild(spinner);
      button.appendChild(document.createTextNode(" " + (label || "Working...")));
    } else {
      button.textContent = button.dataset.label;
    }
  }

  function markField(name, invalid) {
    var field = root.querySelector('[data-field="' + name + '"]');
    if (field) { field.setAttribute("data-invalid", invalid ? "true" : "false"); }
  }

  function validEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());
  }

  function digits(value) {
    return String(value || "").replace(/[^\d+]/g, "");
  }

  /* ------------------------------------------------------------ telemetry */

  /* An opaque per-browser id so the funnel counts people rather than clicks.
     It identifies nobody: random, local to this device, and never joined to a
     signup record. Everything here is wrapped so that telemetry can never be
     the reason a visitor cannot claim their gift. */
  var VISIT_KEY = "sturdy_gift_visit";

  function visitId() {
    try {
      var id = window.localStorage.getItem(VISIT_KEY);
      if (!id) {
        id = Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
        window.localStorage.setItem(VISIT_KEY, id);
      }
      return id;
    } catch (error) { return null; }
  }

  function track(event, step) {
    if (!config.api) { return; }
    var id = visitId();
    if (!id) { return; }
    try {
      window.fetch(config.api + "/api/v1/event", {
        method: "POST",
        keepalive: true,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          visit_id: id,
          event: event,
          step: step || null,
          device: window.matchMedia("(max-width: 767px)").matches ? "mobile" : "desktop"
        })
      }).catch(function () { /* never surfaced */ });
    } catch (error) { /* never surfaced */ }
  }

  function currentStep() {
    return Number(root.getAttribute("data-step")) || 1;
  }

  async function api(path, payload) {
    if (!config.api) {
      throw new Error("The subscription service is not configured yet.");
    }
    var response = await fetch(config.api + path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });
    var data = {};
    try { data = await response.json(); } catch (error) { /* no body */ }
    if (!response.ok) { throw new Error(data.message || "Something went wrong. Please try again."); }
    return data;
  }

  /* ------------------------------------------------------------ open/close */

  function focusable() {
    return Array.prototype.slice
      .call(dialog.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'))
      .filter(function (node) { return !node.disabled && node.offsetParent !== null; });
  }

  function trapFocus(event) {
    if (event.key === "Escape") { close("escape"); return; }
    if (event.key !== "Tab") { return; }
    var items = focusable();
    if (!items.length) { return; }
    var first = items[0];
    var last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }

  var launcher = document.getElementById("sturdy-gift-launcher");

  /* The launcher is the way back in after a close, so it ignores the dismissal
     window — but never appears once someone has their gift. */
  function updateLauncher() {
    if (!launcher) { return; }
    var done = readState().subscribed;
    launcher.hidden = session.opened || done;
  }

  if (launcher) {
    launcher.addEventListener("click", function () {
      open();
      updateLauncher();
    });
  }

  function open() {
    if (session.opened) { return; }
    session.opened = true;
    session.lastFocus = document.activeElement;
    root.hidden = false;
    root.setAttribute("data-open", "true");
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", trapFocus);
    updateLauncher();
    track("shown", currentStep());
    window.setTimeout(function () {
      var target = panes[4].getAttribute("data-active") === "true"
        ? root.querySelector('[data-action="copy-code"]')
        : (panes[2].getAttribute("data-active") === "true" ? otpInputs[0] : detailsForm.elements.first_name);
      if (target) { target.focus(); }
    }, 260);
  }

  /* A close before the reward is revealed is a dismissal; after it, the visitor
     is done and should never be asked again. */
  function close(reason) {
    root.setAttribute("data-open", "false");
    root.hidden = true;
    session.opened = false;
    document.body.style.overflow = "";
    document.removeEventListener("keydown", trapFocus);
    if (session.lastFocus && session.lastFocus.focus) { session.lastFocus.focus(); }

    if (reason !== "subscribed") {
      /* Recorded with the step they left on — that is the number that says
         which part of the flow is losing people. */
      track("dismissed", currentStep());
      writeState({ dismissedUntil: Date.now() + config.dismissDays * 86400000 });
    }
    updateLauncher();
  }

  /* ---------------------------------------------------------------- steps */

  function goTo(step) {
    root.setAttribute("data-step", String(step));
    Object.keys(panes).forEach(function (key) {
      panes[key].setAttribute("data-active", String(Number(key) === step));
    });
    Array.prototype.forEach.call(stepItems, function (item) {
      var index = Number(item.dataset.step);
      item.setAttribute("data-state", index === step ? "current" : (index < step ? "done" : "todo"));
    });
    var focusTarget = step === 2 ? otpInputs[0] : (step === 4 ? root.querySelector('[data-action="copy-code"]') : null);
    if (focusTarget) { window.setTimeout(function () { focusTarget.focus(); }, 220); }
  }

  function startResendCountdown() {
    var remaining = RESEND_SECONDS;
    resendButton.disabled = true;
    window.clearInterval(session.resendTimer);
    resendButton.textContent = "Resend code in " + remaining + "s";
    session.resendTimer = window.setInterval(function () {
      remaining -= 1;
      if (remaining <= 0) {
        window.clearInterval(session.resendTimer);
        resendButton.disabled = false;
        resendButton.textContent = "Resend code";
      } else {
        resendButton.textContent = "Resend code in " + remaining + "s";
      }
    }, 1000);
  }

  /* --------------------------------------------------------------- step 1 */

  /* The dropdown is the source of truth; the section setting is only the
     starting selection, and a theme without the select falls back to it. */
  function countryCode() {
    var select = detailsForm.elements.country_code;
    var value = select ? select.value : config.countryCode;
    return String(value || config.countryCode).replace(/\D/g, "");
  }

  function checkedValues(name) {
    return Array.prototype.slice
      .call(root.querySelectorAll('input[name="' + name + '"]:checked'))
      .filter(function (input) { return input.offsetParent !== null; })
      .map(function (input) { return input.value; });
  }

  function collectDetails() {
    return {
      first_name: detailsForm.elements.first_name.value.trim(),
      email: detailsForm.elements.email.value.trim(),
      phone: digits(detailsForm.elements.phone.value),
      country_code: countryCode(),
      consent: detailsForm.elements.consent.checked,
      page: window.location.pathname
    };
  }

  function validateDetails(payload) {
    var problems = [];

    markField("first_name", !payload.first_name.length);
    if (!payload.first_name.length) { problems.push("first_name"); }

    markField("email", !validEmail(payload.email));
    if (!validEmail(payload.email)) { problems.push("email"); }

    var national = payload.phone.replace(/\D/g, "").replace(/^0+/, "");
    var phoneOk = national.length >= 6 && national.length <= 14;
    markField("phone", !phoneOk);
    if (!phoneOk) { problems.push("phone"); }

    markField("consent", !payload.consent);
    if (!payload.consent) { problems.push("consent"); }

    return problems;
  }

  detailsForm.addEventListener("submit", async function (event) {
    event.preventDefault();
    var button = detailsForm.querySelector('[data-role="submit-details"]');
    var payload = collectDetails();
    var problems = validateDetails(payload);

    if (problems.length) {
      message(1, problems.indexOf("consent") > -1 && problems.length === 1
        ? "Please tick the consent box so we can send your gift."
        : "Please check the highlighted fields.");
      var firstBad = root.querySelector('[data-field="' + problems[0] + '"] input');
      if (firstBad) { firstBad.focus(); }
      return;
    }

    message(1, "");
    busy(button, true, "Sending code...");
    try {
      var result = await api("/api/v1/subscribe", payload);
      session.id = result.subscription_id;
      track("code_sent", 1);
      writePending({ id: result.subscription_id, masked: result.masked_phone });
      maskedPhone.textContent = result.masked_phone || "";
      otpInputs.forEach(function (input) { input.value = ""; });
      goTo(2);
      startResendCountdown();
      message(2, "Code sent. It expires in 10 minutes.", "muted");
    } catch (error) {
      message(1, error.message);
    }
    busy(button, false);
  });

  detailsForm.addEventListener("input", function (event) {
    var field = event.target.closest("[data-field]");
    if (field) { field.setAttribute("data-invalid", "false"); }
    track("started", 1);
  });

  detailsForm.addEventListener("change", function (event) {
    var field = event.target.closest("[data-field]");
    if (field) { field.setAttribute("data-invalid", "false"); }
  });

  /* --------------------------------------------------------------- step 2 */

  function otpValue() {
    return otpInputs.map(function (input) { return input.value.replace(/\D/g, ""); }).join("");
  }

  function submitVerifyForm() {
    // requestSubmit is missing on Safari below 16.
    if (typeof verifyForm.requestSubmit === "function") { verifyForm.requestSubmit(); }
    else { verifyForm.dispatchEvent(new Event("submit", { bubbles: true, cancelable: true })); }
  }

  otpInputs.forEach(function (input, index) {
    input.addEventListener("input", function () {
      input.value = input.value.replace(/\D/g, "").slice(-1);
      root.querySelector('[data-role="otp"]').setAttribute("data-invalid", "false");
      if (input.value && index < otpInputs.length - 1) { otpInputs[index + 1].focus(); }
      if (otpValue().length === otpInputs.length) { submitVerifyForm(); }
    });

    input.addEventListener("keydown", function (event) {
      if (event.key === "Backspace" && !input.value && index > 0) { otpInputs[index - 1].focus(); }
      if (event.key === "ArrowLeft" && index > 0) { otpInputs[index - 1].focus(); }
      if (event.key === "ArrowRight" && index < otpInputs.length - 1) { otpInputs[index + 1].focus(); }
    });

    input.addEventListener("paste", function (event) {
      var text = (event.clipboardData || window.clipboardData).getData("text").replace(/\D/g, "");
      if (!text) { return; }
      event.preventDefault();
      otpInputs.forEach(function (box, position) { box.value = text[position] || ""; });
      otpInputs[Math.min(text.length, otpInputs.length) - 1].focus();
      if (otpValue().length === otpInputs.length) { submitVerifyForm(); }
    });
  });

  verifyForm.addEventListener("submit", async function (event) {
    event.preventDefault();
    var button = verifyForm.querySelector('[data-role="submit-code"]');
    var code = otpValue();
    var otpBox = root.querySelector('[data-role="otp"]');

    if (code.length !== otpInputs.length) {
      otpBox.setAttribute("data-invalid", "true");
      message(2, "Enter all six digits.");
      return;
    }

    message(2, "");
    busy(button, true, "Verifying...");
    try {
      var result = await api("/api/v1/verify", { subscription_id: session.id, code: code });
      session.gift = result.gift || {};
      track("verified", 2);
      goTo(3);
    } catch (error) {
      otpBox.setAttribute("data-invalid", "true");
      otpInputs.forEach(function (input) { input.value = ""; });
      otpInputs[0].focus();
      message(2, error.message);
    }
    busy(button, false);
  });

  /* --------------------------------------------------------------- step 3 */

  /* Every answer is optional. The gift already exists, so a failure to record
     these must never stand between the visitor and their code — on any error
     we reveal anyway and let the sync queue catch up. */
  profileForm.addEventListener("submit", async function (event) {
    event.preventDefault();
    var button = profileForm.querySelector('[data-role="submit-profile"]');
    busy(button, true, "Just a sec...");
    try {
      await api("/api/v1/profile", {
        subscription_id: session.id,
        gift_lane: checkedValues("gift_lane"),
        level: checkedValues("level"),
        sport: checkedValues("sport")
      });
    } catch (error) { /* answers are a nicety; the gift is not */ }
    track("profiled", 3);
    busy(button, false);
    reveal(session.gift || {});
  });

  /* --------------------------------------------------------------- step 4 */

  function confetti() {
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) { return; }
    var box = root.querySelector('[data-role="confetti"]');
    var colours = ["#bf0200", "#ff4d47", "#171717", "#ffe3e1", "#f59e0b"];
    box.innerHTML = "";
    for (var i = 0; i < 70; i += 1) {
      var piece = document.createElement("span");
      piece.style.left = (Math.random() * 100).toFixed(2) + "%";
      piece.style.setProperty("--sr-piece", colours[i % colours.length]);
      piece.style.setProperty("--sr-delay", (Math.random() * 0.7).toFixed(2) + "s");
      piece.style.setProperty("--sr-duration", (1.8 + Math.random() * 1.6).toFixed(2) + "s");
      piece.style.setProperty("--sr-drift", (Math.random() * 240 - 120).toFixed(0) + "px");
      box.appendChild(piece);
    }
    window.setTimeout(function () { box.innerHTML = ""; }, 4200);
  }

  function reveal(gift) {
    /* The gift link applies the discount and then lands them on the store, so
       the CTA is one click rather than copy-paste-navigate. Falls back to the
       button's configured href if the service did not return a link. */
    var cta = root.querySelector('[data-role="gift-cta"]');
    if (cta && gift.link) { cta.href = gift.link; }

    root.querySelector('[data-role="gift-label"]').textContent = gift.label || "Your gift";
    root.querySelector('[data-role="gift-code"]').textContent = gift.code || "";
    root.querySelector('[data-role="gift-terms"]').textContent = gift.terms || "";
    goTo(4);
    track("revealed", 4);
    confetti();
    writeState({ subscribed: true, subscribedAt: Date.now(), dismissedUntil: 0 });
    writePending(null);
  }

  /* -------------------------------------------------------------- actions */

  root.addEventListener("click", async function (event) {
    var trigger = event.target.closest("[data-action]");
    if (!trigger) { return; }
    var action = trigger.dataset.action;

    if (action === "close") {
      close(panes[3].getAttribute("data-active") === "true" ? "subscribed" : "closed");
    }

    if (action === "edit-details") {
      goTo(1);
      message(1, "");
    }

    if (action === "resend") {
      busy(trigger, true, "Sending...");
      try {
        await api("/api/v1/resend", { subscription_id: session.id });
        message(2, "New code sent.", "success");
        startResendCountdown();
      } catch (error) {
        message(2, error.message);
      }
      busy(trigger, false);
      if (!resendButton.disabled) { resendButton.textContent = "Resend code"; }
    }

    if (action === "copy-code") {
      var code = root.querySelector('[data-role="gift-code"]').textContent.trim();
      if (code && navigator.clipboard) {
        navigator.clipboard.writeText(code).then(function () {
          trigger.textContent = "Copied";
          window.setTimeout(function () { trigger.textContent = "Copy code"; }, 2000);
        });
      }
    }
  });

  /* No click-outside-to-close: the only ways out are the X and Escape, so a
     half-filled form is never lost to a stray click. Escape stays because a
     modal dialog must be dismissible from the keyboard. */

  /* -------------------------------------------------------------- trigger */

  function scrolledPercent() {
    var height = document.documentElement.scrollHeight - window.innerHeight;
    if (height <= 0) { return 100; }
    return (window.scrollY / height) * 100;
  }

  function watch() {
    /* The launcher shows even when the popup itself is suppressed: that is the
       whole point of it — a way back after an accidental close. */
    updateLauncher();
    if (suppressed()) { return; }

    var readyAt = Date.now() + config.minSeconds * 1000;
    var pending = readPending();

    /* A reload mid-verification drops straight back to step 2. */
    if (pending && pending.id) {
      session.id = pending.id;
      maskedPhone.textContent = pending.masked || "";
      goTo(2);
      startResendCountdown();
    }

    function maybeOpen() {
      if (session.opened) { return; }
      if (Date.now() < readyAt) { return; }
      if (scrolledPercent() < config.scrollPercent) { return; }
      window.removeEventListener("scroll", maybeOpen);
      open();
    }

    window.addEventListener("scroll", maybeOpen, { passive: true });
    /* Short pages that cannot scroll still deserve the offer. */
    window.setTimeout(maybeOpen, Math.max(config.minSeconds, 1) * 1000 + 400);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", watch);
  } else {
    watch();
  }

  /* Exposed for QA: sturdyGiftPopup.open() / .reset() from the console. */
  window.sturdyGiftPopup = {
    open: open,
    close: close,
    reset: function () {
      try { window.localStorage.removeItem(STORE_KEY); window.sessionStorage.removeItem(SESSION_KEY); } catch (error) { /* noop */ }
      goTo(1);
    }
  };
})();
